if (!customElements.get('hero-slider')) {
  class HeroSlider extends HTMLElement {
    constructor() {
      super();
      this.onPillClick = this.onPillClick.bind(this);
      this.onPause = this.pause.bind(this);
      this.onResume = this.resume.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
    }

    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.hero-slider__slide'));
      this.pills = Array.from(this.querySelectorAll('.hero-slider__pill'));
      this.count = this.slides.length;
      this.index = 0;
      this.timer = null;
      this.speed = (parseFloat(this.dataset.speed) || 6) * 1000;

      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplay = this.dataset.autoplay === 'true' && this.count > 1 && !reduced;

      if (this.count <= 1) return;

      this.pills.forEach((pill) => pill.addEventListener('click', this.onPillClick));
      this.addEventListener('mouseenter', this.onPause);
      this.addEventListener('mouseleave', this.onResume);
      this.addEventListener('focusin', this.onPause);
      this.addEventListener('focusout', this.onResume);
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      if (this.autoplay) this.start();
    }

    disconnectedCallback() {
      this.clearTimer();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }

    onPillClick(event) {
      const target = event.currentTarget;
      const i = parseInt(target.dataset.index, 10);
      if (Number.isNaN(i)) return;
      this.goTo(i, true);
    }

    onVisibilityChange() {
      if (document.hidden) this.pause();
      else this.resume();
    }

    goTo(targetIndex, userInitiated) {
      if (this.count <= 1) return;
      const next = ((targetIndex % this.count) + this.count) % this.count;
      const prev = this.index;
      if (next === prev && !userInitiated) {
        this.restartActiveFill();
        return;
      }

      this.slides[prev].classList.remove('is-active');
      this.slides[prev].setAttribute('aria-hidden', 'true');
      this.slides[next].classList.add('is-active');
      this.slides[next].setAttribute('aria-hidden', 'false');

      this.pills.forEach((pill, idx) => {
        pill.classList.toggle('is-active', idx === next);
        pill.classList.toggle('is-completed', idx < next);
        pill.setAttribute('aria-selected', idx === next ? 'true' : 'false');
      });

      this.index = next;
      this.restartActiveFill();

      if (userInitiated && this.autoplay) {
        this.clearTimer();
        this.start();
      }
    }

    restartActiveFill() {
      const activePill = this.pills[this.index];
      if (!activePill) return;
      const fill = activePill.querySelector('.hero-slider__pill-fill');
      if (!fill) return;
      fill.style.animation = 'none';
      // Force reflow to restart the CSS animation cleanly.
      void fill.offsetWidth;
      fill.style.animation = '';
      fill.style.animationPlayState = '';
    }

    start() {
      if (!this.autoplay) return;
      this.clearTimer();
      this.timer = window.setTimeout(() => {
        this.goTo(this.index + 1, false);
        this.start();
      }, this.speed);
    }

    pause() {
      this.clearTimer();
      const activeFill = this.querySelector('.hero-slider__pill.is-active .hero-slider__pill-fill');
      if (activeFill) activeFill.style.animationPlayState = 'paused';
    }

    resume() {
      if (!this.autoplay || this.timer) return;
      const activeFill = this.querySelector('.hero-slider__pill.is-active .hero-slider__pill-fill');
      if (activeFill) activeFill.style.animationPlayState = 'running';
      this.start();
    }

    clearTimer() {
      if (this.timer) {
        window.clearTimeout(this.timer);
        this.timer = null;
      }
    }
  }

  customElements.define('hero-slider', HeroSlider);
}

// Theme editor integration: reflect block selection in the slider.
document.addEventListener('shopify:block:select', (event) => {
  const slider = event.target.closest('hero-slider');
  if (!slider) return;
  const idx = parseInt(event.target.dataset.index, 10);
  if (!Number.isNaN(idx)) slider.goTo(idx, true);
  slider.pause();
});

document.addEventListener('shopify:block:deselect', (event) => {
  const slider = event.target.closest('hero-slider');
  if (!slider) return;
  slider.resume();
});
